import { useEffect, useState, useRef } from 'react'
import { NextSeo } from 'next-seo'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import { useRouter } from 'next/router'

import { Typography, Button } from '@evocolabs/orkaui'
import { useSelector } from 'react-redux'
import Header from '@/src/components/HeaderV2'
import RequireLogin from '@/src/utils/RequireLogin'
import SpinningCircle from '@/src/components/SpinningCircle'

import { selector } from '@/src/redux/store'
import { EMAIL } from '@/src/redux/state'
import { useBackend } from '@/src/providers/BackendProvider'

import styles from '@/src/components/scss/LegalData.module.scss'

const View1 = ({ setView }) => {
  const { requestDataExport } = useBackend()
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  const submit = async () => {
    await requestDataExport(loggedUser)
    setView(2)
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Export Personal Data</Typography>
      <Typography variant="body3">
        Your personal information such as your name, year of birth, and email
        will be emailed to you as a file. <br />
        The file will be sent to the following email address{' '}
        <Typography variant="Body3">
          {useSelector(selector.selectUserInfo)[EMAIL]}
        </Typography>
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          loadable
          id="export-personal-data-page-export-button"
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

const View2 = () => {
  const submit = async () => {
    window.location.href = '/'
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Submitted</Typography>
      <Typography variant="body3">
        Your request is being processed. Upon completion, we will notify you via
        email or text.
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          id="export-personal-data-submission-success-button"
        >
          OK
        </Button>
      </div>
    </div>
  )
}

const Expired = () => {
  const submit = async () => {
    window.location.href = '/legal/ExportPersonalData'
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Error</Typography>
      <Typography variant="body3">
        Your request to download your personal data has expired. Please try
        again.
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          id="export-personal-data-page-request-again-button"
        >
          Request Again
        </Button>
      </div>
    </div>
  )
}

const Export = () => {
  const [data, setData] = useState(false)
  const {
    getAllPersonalData,
    userDetails,
    getAllOrderDetails,
    getLatestHearingCondition
  } = useBackend()
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  const download = async () => {
    const dataString = JSON.stringify(data, null, 4)
    const blob = new Blob([dataString], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = 'personal_data.json'
    link.href = url
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  useEffect(() => {
    ;(async () => {
      const order = await getAllOrderDetails()
      const hearingCondition = await getLatestHearingCondition(loggedUser)
      const allPersonalData = getAllPersonalData(
        userDetails,
        order,
        hearingCondition?.hearingCondition?.answer
      )
      setData(allPersonalData)
    })()
  }, [userDetails])
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Download Your Personal Data</Typography>
      {!data ? (
        <div className={styles.spinnerWrapper}>
          <SpinningCircle />
          <Typography variant="body3">
            We are processing your request.
          </Typography>
        </div>
      ) : (
        <>
          <Typography variant="body3">
            Your files are ready, please click the button below to download.
          </Typography>
          <div className={styles.buttonWrapper}>
            <Button
              variant="primary"
              size="large"
              handleClick={download}
              id="export-personal-data-page-download-button"
            >
              Download
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

const ExportPersonalData = () => {
  const router = useRouter()
  const token = router.query.token
  const [view, setView] = useState(-1)
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  const validToken = useRef(true)
  if (token) {
    const decoded = jwtDecode(token)
    // Check if token is expired
    const tokenDate = new Date(decoded.exp * 1000)
    const dateDiff = moment().diff(tokenDate, 'seconds')
    if (dateDiff > 0 && view !== 'expired') {
      setView('expired')
      validToken.current = false
    }
    // Check if token uuid matches user uuid
    if (loggedUser !== decoded.user_id) {
      validToken.current = false
      alert(
        'You are not authorized to access this page. Please sign in with the account you used to request the data.'
      )
      router.push(
        '/SignIn' +
          `?redirect_uri=${encodeURIComponent(
            location.origin + location.pathname + location.search
          )}`
      )
    }
    if (view !== 'export' && validToken.current) {
      setView('export')
    }
  } else {
    if (view === -1) {
      setView(1)
    }
  }
  return (
    <RequireLogin>
      <style>{`html, body, #vh-relative { background-color: #F7F3EB !important;}`}</style>
      <NextSeo
        title="Export Personal Data | Orka - Hear what matters"
        description="Export Personal Data"
      />
      <Header isWhiteBg />
      {view === 1 && <View1 setView={setView} />}
      {view === 2 && <View2 />}
      {view === 'expired' && <Expired />}
      {view === 'export' && <Export />}
    </RequireLogin>
  )
}

export default ExportPersonalData
